
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateType} from './ConfigUpdateType';
import {FieldModelConfigUpdateType} from './ConfigUpdateType';

/**
 * ConfigUpdate struct
 */
class ConfigUpdate {
  /**
   * Initialize struct
   * @param {!ConfigUpdateType=} updateType
   * @param {!string=} key
   * @param {!string=} content
   * @param {!number=} version
   * @constructor
   */
  constructor (updateType = new ConfigUpdateType(), key = '', content = '', version = new UInt64(0, 0)) {
    this.UpdateType = updateType
    this.Key = key
    this.Content = content
    this.Version = version
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigUpdate}
   * @param {!ConfigUpdate} other Other struct
   * @returns {!ConfigUpdate} This struct
   */
  copy (other) {
    if (other.UpdateType != null) {
      this.UpdateType = ConfigUpdateType.fromObject(other.UpdateType)
    } else {
      this.UpdateType = null
    }
    if (other.Key != null) {
      this.Key = other.Key
    } else {
      this.Key = null
    }
    if (other.Content != null) {
      this.Content = other.Content
    } else {
      this.Content = null
    }
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigUpdate}
   * @returns {!ConfigUpdate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigUpdateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigUpdateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigUpdate}
   * @param {!ConfigUpdate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigUpdate)) {
      throw new TypeError('Instance of ConfigUpdate is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigUpdate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      UpdateType: ((this.UpdateType != null) ? this.UpdateType : null),
      Key: ((this.Key != null) ? this.Key : null),
      Content: ((this.Content != null) ? this.Content : null),
      Version: ((this.Version != null) ? this.Version.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigUpdate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigUpdate} other Object value
   * @returns {!ConfigUpdate} Created struct
   */
  static fromObject (other) {
    return new ConfigUpdate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigUpdate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigUpdate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigUpdate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1009
  }
}

export { ConfigUpdate };

/**
 * Fast Binary Encoding ConfigUpdate field model
 */
class FieldModelConfigUpdate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.UpdateType = new FieldModelConfigUpdateType(buffer, 4 + 4)
    this.Key = new fbe.FieldModelString(buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize)
    this.Content = new fbe.FieldModelString(buffer, this.Key.fbeOffset + this.Key.fbeSize)
    this.Version = new fbe.FieldModelUInt64(buffer, this.Content.fbeOffset + this.Content.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.UpdateType.fbeSize + this.Key.fbeSize + this.Content.fbeSize + this.Version.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.UpdateType.fbeExtra + this.Key.fbeExtra + this.Content.fbeExtra + this.Version.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigUpdate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1009
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigUpdate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigUpdate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Key.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Key.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.Content.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Content.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Content.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigUpdate}
   * @param {!ConfigUpdate} fbeValue Default value, defaults is new ConfigUpdate()
   * @returns {!ConfigUpdate} ConfigUpdate value
   */
  get (fbeValue = new ConfigUpdate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigUpdate}
   * @param {!ConfigUpdate} fbeValue ConfigUpdate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateType = this.UpdateType.get()
    } else {
      fbeValue.UpdateType = new ConfigUpdateType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Key.fbeSize) <= fbeStructSize) {
      fbeValue.Key = this.Key.get()
    } else {
      fbeValue.Key = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.Content.fbeSize) <= fbeStructSize) {
      fbeValue.Content = this.Content.get()
    } else {
      fbeValue.Content = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Content.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigUpdate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigUpdate}
   * @param {!ConfigUpdate} fbeValue ConfigUpdate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigUpdate}
   * @param {ConfigUpdate} fbeValue ConfigUpdate value
   */
  setFields (fbeValue) {
    this.UpdateType.set(fbeValue.UpdateType)
    this.Key.set(fbeValue.Key)
    this.Content.set(fbeValue.Content)
    this.Version.set(fbeValue.Version)
  }
}

export { FieldModelConfigUpdate };

/**
 * Fast Binary Encoding ConfigUpdate model
 */
class ConfigUpdateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigUpdate(this.buffer, 4)
  }

  /**
   * Get the ConfigUpdate model
   * @this {!ConfigUpdateModel}
   * @returns {!FieldModelConfigUpdate} model ConfigUpdate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigUpdateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigUpdateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigUpdateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigUpdateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigUpdate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigUpdateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigUpdateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigUpdateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigUpdateModel}
   * @param {!ConfigUpdate} value ConfigUpdate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigUpdateModel}
   * @param {!ConfigUpdate} value ConfigUpdate value, defaults is new ConfigUpdate()
   * @return {!object} Deserialized ConfigUpdate value and its size
   */
  deserialize (value = new ConfigUpdate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigUpdate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigUpdate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigUpdateModel}
   * @param {!number} prev Previous ConfigUpdate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigUpdateModel };
